import { useCallback, useMemo } from 'react'

import { EllipsisVertical } from 'lucide-react'
import Image from 'next/image'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'

import { ErrorMessages } from '~/components/FormErrorMessages'
import { Group, List, TextInput } from '~/components/component-library'
import { Dropdown } from '~/components/component-library/Dropdown'
import { StepButtons } from '~/components/create-team/StepButtons'
import {
  DeletePasskeyDialogContent,
  RenamePasskeyDialogContent,
} from '~/components/settings/PasskeysEdit'
import { PhotoEdit } from '~/components/settings/PhotoEdit'
import { useAuth } from '~/context/AuthProvider'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useAddNewPasskey } from '~/hooks/useAddNewPasskey'
import { useAuthUserUpdates, useRequiredAuthUser } from '~/hooks/useAuthUser'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgPasskey } from '~/types'
import { isDarkMode } from '~/utils'

type ProfileFormValues = {
  displayName: string
}

export function ProfileStep({
  stepForward,
  stepBack,
}: {
  stepForward: () => void
  stepBack: () => void
}) {
  const user = useRequiredAuthUser()
  const authUpdates = useAuthUserUpdates()
  const { setGlobalModalState } = useAuth()
  const org = useRequiredActiveOrg()
  const { addNewPasskey } = useAddNewPasskey()

  const { serverPostRequest, isProcessing: isProcessingCreate } =
    useServerRequests()
  const { serverPutRequest, isProcessing: isProcessingEdit } =
    useServerRequests()

  const { control, handleSubmit, formState } = useForm<ProfileFormValues>({
    defaultValues: {
      displayName: user.displayName,
    },
  })

  const userPasskeys = useMemo(
    () => org.passkeys.filter((p) => p.user?.id === user.id) as OrgPasskey[],
    [org.passkeys, user.id],
  )

  const displayName = useWatch({ control, name: 'displayName' })

  const onSubmit: SubmitHandler<ProfileFormValues> = useCallback(
    async ({ displayName }) => {
      // Update display name if changed
      if (displayName !== user.displayName) {
        await serverPutRequest({
          path: '/api/user/edit/display_name',
          params: { name: displayName },
          errorMessage: 'Failed to update name',
        }).then(() => {
          authUpdates.updateDisplayName(displayName)
        })
      }

      stepForward()
    },
    [authUpdates, serverPutRequest, stepForward, user.displayName],
  )

  /**
   * Updates the user's profile photo url
   * @param file - The new profile photo
   */
  function updateProfilePhotoUrl(file: File) {
    const formData = new FormData()
    formData.append('profile_photo', file)

    return serverPostRequest<{ profilePhotoUrl: string }>({
      path: '/api/user/edit/profile_photo/upload',
      params: formData,
      processingMessage: 'Updating photo...',
      errorMessage: 'Failed to update photo',
    }).then((result) => {
      /** Update the profile photo url in the cache */
      if (result) {
        authUpdates.updateProfilePhotoUrl(result.data.profilePhotoUrl)
      }
    })
  }

  return (
    <>
      <TextInput
        control={control}
        inputName="displayName"
        placeholder="Vitalik Buterin"
        autoFocus
        label="Display name"
        autoComplete="off"
      />
      <Group label="Profile photo">
        <PhotoEdit
          photoUrl={user.profilePhotoUrl}
          photoAlt={'Profile photo'}
          processFile={updateProfilePhotoUrl}
          shape="circular"
          size="md"
        />
      </Group>
      <List
        label="Passkeys"
        footer="Used to approve transactions. Add at least one to start."
        cta={{ label: 'Add passkey', onClick: addNewPasskey }}
        items={[
          ...userPasskeys.map((p) => {
            const iconSrc = isDarkMode()
              ? p.providerIconDark
              : p.providerIconLight

            return {
              key: p.id,
              content: p.name,
              image: iconSrc ? (
                <Image
                  src={iconSrc}
                  alt={`Icon for ${p.name}`}
                  width={20}
                  height={20}
                />
              ) : (
                <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-700" />
              ),
              actionIcon: (
                <div className="flex items-center gap-2">
                  <Dropdown
                    side="bottom"
                    items={[
                      {
                        content: 'Rename...',
                        onClick: () =>
                          setGlobalModalState({
                            isOpen: true,
                            content: (
                              <RenamePasskeyDialogContent
                                key={p.id}
                                passkey={p}
                                closeDialog={() =>
                                  setGlobalModalState({
                                    isOpen: false,
                                    content: null,
                                  })
                                }
                              />
                            ),
                          }),
                      },
                      {
                        content: 'Remove...',
                        onClick: () =>
                          setGlobalModalState({
                            isOpen: true,
                            content: (
                              <DeletePasskeyDialogContent
                                key={p.id}
                                passkey={p}
                                closeDialog={() =>
                                  setGlobalModalState({
                                    isOpen: false,
                                    content: null,
                                  })
                                }
                              />
                            ),
                          }),
                      },
                    ]}
                  >
                    <div className="flex h-5 w-5 items-center">
                      <EllipsisVertical className="h-4 w-4 cursor-pointer text-secondary" />
                    </div>
                  </Dropdown>
                </div>
              ),
            }
          }),
        ]}
      />
      <ErrorMessages errors={formState.errors} />
      <StepButtons
        isDisabled={
          isProcessingCreate ||
          isProcessingEdit ||
          !displayName?.trim() ||
          !user.profilePhotoUrl ||
          userPasskeys.length === 0
        }
        isLoading={isProcessingEdit}
        onBackClick={stepBack}
        onNextClick={handleSubmit(onSubmit)}
      />
    </>
  )
}
